import { useEffect } from 'react';
import { getKeyHandler } from 't-lib-ui/structure';
import { isNativeApp, nativePlatform } from '../utils/platform';
import './GoogleLoginButton.css';

interface GoogleLoginButtonProps {
  onSuccess: (credential: string) => void;
  onError: (error: string) => void;
  label?: string;
  sideway?: boolean; // reached with left/right only (FlowForm 'sidewayFocus'), next to another button
}

const csrfState = Math.random().toString(36).substring(0, 10);

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
// Not in the iOS app yet: it needs Google's iOS sign-in SDK. See cordova/IOS-TODO.md.
export const googleLoginEnabled = !!googleClientId && !googleClientId.startsWith('<') && nativePlatform != 'ios';

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({ onSuccess, onError, label = 'Sign in with Google', sideway }) => {
  const enabled = googleLoginEnabled;

  useEffect(() => {
    if (!enabled) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin != window.location.origin || event.data?.type != 'google-auth') return;
      if (event.data.error)
        onError(event.data.error);
      else if (event.data.idToken)
        onSuccess(event.data.idToken);
      else
        onError('No credential received from Google');
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [enabled, onSuccess, onError]);

  if (!enabled) return null;

  const handleClick = () => {
    // Google refuses OAuth inside embedded WebViews (disallowed_useragent), so the app asks Android's
    // account picker instead (cordova/local-plugins/google-signin). Its token is issued for the same
    // web client id, so the auth server treats it like one from the popup. Cancelling isn't an error.
    if (isNativeApp) {
      (window as any).cordova.plugins.googleSignIn.signIn(googleClientId)
        .then(onSuccess, (error: string) => error != 'cancelled' && onError(error));
      return;
    }
    const nonce = crypto.randomUUID();
    const redirectUri = `${window.location.origin}/google-callback.html`;
    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: 'id_token',
      scope: 'email',
      state: csrfState, // random state to prevent CSRF
      nonce,
      prompt: 'select_account',
    });
    const width = 500, height = 600;
    const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
    console.log('Opening Google login popup with params:', params.toString(), `${GOOGLE_AUTH_URL}?${params}`);
    window.open(`${GOOGLE_AUTH_URL}?${params}`, 'google-login', `width=${width},height=${height},left=${left},top=${top}`);
  };

  const handleKeyDown = getKeyHandler(() => handleClick());

  return (
    <div className="googleLoginButton">
      <button className={`googleLoginCustomButton flowFocussable${sideway ? ' sidewayFocus' : ''}`} onClick={handleClick} onKeyDown={handleKeyDown} type="button">
        <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        <span>{label}</span>
      </button>
    </div>
  );
};

export default GoogleLoginButton;
